import React from 'react';
import { motion } from 'framer-motion';

export interface CardProps {
  children: React.ReactNode;
  variant?: 'default' | 'glass' | 'elevated' | 'outlined' | 'premium' | 'glassPremium';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  className?: string;
  hover?: boolean;
  onClick?: () => void;
}

const Card: React.FC<CardProps> = ({
  children,
  variant = 'default',
  padding = 'md',
  className = '',
  hover = false,
  onClick,
  ...props
}) => {
  // Updated to rounded-2xl for consistency with StatTile
  const baseClasses = 'rounded-2xl transition-all duration-200';
  
  const variantClasses = {
    default: 'bg-white border border-primary-500/10 shadow-card',
    glass: 'bg-white/90 backdrop-blur-lg border border-primary-500/10 shadow-elevated',
    elevated: 'bg-white border border-primary-500/10 shadow-elevated hover:shadow-card-hover',
    outlined: 'bg-white border-2 border-primary-500/20 shadow-none hover:border-accent-500',
    // Updated premium variant to match StatTile styling:
    // rounded-2xl (from base), bg-white/90, backdrop-blur-sm, border-primary-500/10, shadow-card, hover:shadow-lg
    premium: 'bg-white/90 backdrop-blur-sm border border-primary-500/10 shadow-card hover:shadow-lg',
    glassPremium: 'backdrop-blur-lg bg-white/10 border border-white/30 shadow-elevated'
  } as const;

  const paddingClasses = {
    none: '',
    sm: 'p-3 sm:p-4',
    md: 'p-4 sm:p-6',
    lg: 'p-6 sm:p-8'
  } as const;

  const hoverClasses = hover ? 'hover:shadow-lg cursor-pointer' : ''; // Updated hover effect
  const clickableClasses = onClick ? 'cursor-pointer' : '';

  // Filter out non-DOM props to prevent React DOM warnings
  const { bgPrimary500, bgWhite, ...domProps } = props as any;

  // Keyboard handler for accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  const combinedClassName = `${baseClasses} ${variantClasses[variant]} ${paddingClasses[padding]} ${hoverClasses} ${clickableClasses} ${className}`;

  if (!hover && !onClick) {
    return (
      <div
        className={combinedClassName}
        {...domProps}
      >
        {children}
      </div>
    );
  }

  return (
    <motion.div
      className={combinedClassName}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      whileHover={hover ? { y: 0 } : {}} // Removed y: -2 to match StatTile better (it handles its own animation) or keep it subtle
      transition={{ duration: 0.2 }}
      {...domProps}
    >
      {children}
    </motion.div>
  );
};

// Card sub-components for structured content
export const CardHeader: React.FC<{ children: React.ReactNode; className?: string }> = ({ 
  children, 
  className = '' 
}) => (
  <div className={`pb-3 border-b border-primary-500/10 mb-3 ${className}`}>
    {children}
  </div>
);

export const CardTitle: React.FC<{ children: React.ReactNode; className?: string }> = ({ 
  children, 
  className = '' 
}) => (
  <h3 className={`text-lg font-semibold text-primary-500 ${className}`}>
    {children}
  </h3>
);

export const CardDescription: React.FC<{ children: React.ReactNode; className?: string }> = ({ 
  children, 
  className = '' 
}) => (
  <p className={`text-sm mt-1 text-primary-400 ${className}`}>
    {children}
  </p>
);

export const CardContent: React.FC<{ children: React.ReactNode; className?: string }> = ({ 
  children, 
  className = '' 
}) => (
  <div className={className}>
    {children}
  </div>
);

export const CardFooter: React.FC<{ children: React.ReactNode; className?: string }> = ({ 
  children, 
  className = '' 
}) => (
  <div className={`pt-3 border-t border-primary-500/10 mt-3 ${className}`}>
    {children}
  </div>
);

export default Card;
